import type { Module } from '@pc/core';
import { probeSchemaIsExposed } from '@pc/db';
import { lazyWithReload } from '@pc/ui';
import { registerFulfillmentShipSlot } from '@pc/modules-orders';
import { FulfillmentShipmentPanel } from './components/FulfillmentShipmentPanel';
import './events';

// All routes use `lazyWithReload` so post-deploy stale shells recover via
// a debounced reload instead of throwing "Failed to fetch dynamically
// imported module".
const FulfillmentQueuePage = lazyWithReload(() => import('./pages/FulfillmentQueuePage'));
const FulfillmentBoardPage = lazyWithReload(() => import('./pages/FulfillmentBoardPage'));
const ShipmentDetailPage = lazyWithReload(() => import('./pages/ShipmentDetailPage'));
const PackingStationPage = lazyWithReload(() => import('./pages/PackingStationPage'));
const DeliveryFeedbackPage = lazyWithReload(() => import('./pages/DeliveryFeedbackPage'));
const DeliveryFeedbackFormPage = lazyWithReload(() => import('./pages/DeliveryFeedbackFormPage'));
const FulfillmentSettingsPage = lazyWithReload(
  () => import('./pages/FulfillmentSettingsPage'),
);

// Register into the orders module's ship slot at import time. This
// REPLACES `shipping.label-panel` (which @pc/modules-shipping previously
// registered) -- the registry de-dupes by id, but for clarity we use a
// distinct id and remove the shipping module's registration in the same
// PR. Order < 10 puts us ahead of any future contender.
registerFulfillmentShipSlot({
  id: 'fulfillment.shipment-panel',
  order: 5,
  render: ({ orderId, orgId, storeId, shipToSnapshot }) => (
    <FulfillmentShipmentPanel
      orderId={orderId}
      orgId={orgId}
      storeId={storeId}
      shipToSnapshot={shipToSnapshot}
    />
  ),
});

export const fulfillmentModule: Module = {
  id: 'fulfillment',
  name: 'Fulfillment',
  description:
    'Warehouse hub queue + packing station for ship-type CRM orders. Branded customer tracking page, opt-in email + SMS updates, and per-org packing requirements.',
  basePath: '/fulfillment',
  routes: [
    { path: '/', element: <FulfillmentQueuePage /> },
    // Static paths MUST stay ahead of `/:id` (route-ordering footgun).
    { path: '/board', element: <FulfillmentBoardPage /> },
    { path: '/feedback', element: <DeliveryFeedbackPage /> },
    { path: '/feedback-form', element: <DeliveryFeedbackFormPage /> },
    { path: '/settings', element: <FulfillmentSettingsPage /> },
    { path: '/pack/:id', element: <PackingStationPage /> },
    { path: '/:id', element: <ShipmentDetailPage /> },
  ],
  navItems: [
    { label: 'Fulfillment', to: '/fulfillment', order: 30 },
    { label: 'Shipment board', to: '/fulfillment/board', order: 31 },
    { label: 'Delivery feedback', to: '/fulfillment/feedback', order: 32 },
    { label: 'Feedback form', to: '/fulfillment/feedback-form', order: 33 },
  ],
  dashboardWidgets: [],
  events: {
    publishes: [
      'fulfillment.shipment_created',
      'fulfillment.label_attached',
      'fulfillment.packed',
      'fulfillment.shipped',
      'fulfillment.delivered',
      'fulfillment.exception',
    ],
  },
  healthProbe: () => probeSchemaIsExposed('fulfillment', 'v_shipment_summary'),
};
